"use strict";
/**
 * actuarial.ts - Actuarial calculations engine for Manulife Universal Life (UL) & Unit-Linked (ILP)
 * Part of @manulife/man-core DDD Domain Layer.
 *
 * Core mathematical simulation engine for:
 *   - Universal Life (Xanh Ước Mơ - XUM)
 *   - Unit-Linked Plans (Xanh Phú Quý - XPQ, Xanh Tương Lai - XTL, XTL-PS)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateULCashflow = exports.getMonthlyMortalityRate = exports.calculateInsuranceAge = exports.calculateChronologicalAge = exports.calculateSAMinMax = exports.getGuaranteedInterestRate = exports.getMonthlyPolicyFee = exports.getSurrenderChargeRate = exports.getInitialChargeRate = exports.getTotalRiderPremium = exports.getRiderAnnualPremium = exports.HEALTH_RIDER_ANNUAL_INCREMENT = exports.HEALTH_RIDER_YEAR_2_INCREMENT = exports.AGE_REPRICED_RIDER_IDS = exports.RIDERS_PREMIUM = exports.RIDERS_BASE_PREMIUM = exports.parseNumber = void 0;
/**
 * Safe numeric parser helper to prevent NaN crashes on UI/Engine calculations.
 */
const parseNumber = (val, fallback = 0) => {
    if (val === undefined || val === null)
        return fallback;
    if (typeof val === 'number')
        return isNaN(val) ? fallback : val;
    const cleaned = String(val).replace(/,/g, '').replace(/\./g, '').trim();
    const parsed = Number(cleaned);
    return isNaN(parsed) ? fallback : parsed;
};
exports.parseNumber = parseNumber;
exports.RIDERS_BASE_PREMIUM = {
    R1: 1790000, // Sống Khỏe Mỗi Ngày
    R2: 284000, // Lá Chắn Xanh
    R3: 1717000, // Dự Phòng Xanh
    R4: 142000, // Hộ Vệ Xanh
    R5: 0, // Bảo Thân Xanh (Complimentary default)
};
exports.RIDERS_PREMIUM = { ...exports.RIDERS_BASE_PREMIUM };
exports.AGE_REPRICED_RIDER_IDS = new Set(['R1', 'R2', 'R4']);
exports.HEALTH_RIDER_YEAR_2_INCREMENT = 10000;
exports.HEALTH_RIDER_ANNUAL_INCREMENT = 40000;
const R1_PREMIUMS_BY_AGE = {
    35: 1790000,
    36: 1800000,
    37: 1840000,
    38: 1880000,
    39: 1920000,
    40: 1960000,
    41: 1990000,
    42: 2030000,
    43: 2070000,
    44: 2110000,
    45: 2150000,
    46: 2210000,
    47: 2290000,
    48: 2370000,
    49: 2460000,
    50: 2540000,
    51: 2600000,
    52: 2810000,
    53: 3020000,
    54: 3240000,
};
const getRiderAnnualPremium = (riderId, policyYear, override, age) => {
    const base = override?.[riderId] ?? exports.RIDERS_BASE_PREMIUM[riderId] ?? 0;
    if (base === 0)
        return 0;
    if (riderId === 'R1' && age !== undefined) {
        const roundedAge = Math.floor(age);
        if (R1_PREMIUMS_BY_AGE[roundedAge] !== undefined) {
            const baseR1Override = override?.['R1'];
            if (baseR1Override !== undefined) {
                const baseR1Silver35 = R1_PREMIUMS_BY_AGE[35] ?? 1790000;
                const multiplier = baseR1Override / baseR1Silver35;
                return Math.round(R1_PREMIUMS_BY_AGE[roundedAge] * multiplier);
            }
            return R1_PREMIUMS_BY_AGE[roundedAge];
        }
    }
    if (!exports.AGE_REPRICED_RIDER_IDS.has(riderId))
        return base;
    if (policyYear <= 1)
        return base;
    if (policyYear === 2)
        return base + exports.HEALTH_RIDER_YEAR_2_INCREMENT;
    return base + exports.HEALTH_RIDER_YEAR_2_INCREMENT + (policyYear - 2) * exports.HEALTH_RIDER_ANNUAL_INCREMENT;
};
exports.getRiderAnnualPremium = getRiderAnnualPremium;
const getTotalRiderPremium = (selectedRiderIds, policyYear, riderPremiumOverrides, age) => {
    return selectedRiderIds.reduce((sum, id) => sum + (0, exports.getRiderAnnualPremium)(id, policyYear, riderPremiumOverrides, age), 0);
};
exports.getTotalRiderPremium = getTotalRiderPremium;
const getInitialChargeRate = (year) => {
    if (year === 1)
        return 0.50;
    if (year === 2)
        return 0.30;
    if (year === 3)
        return 0.20;
    if (year === 4)
        return 0.20;
    if (year === 5)
        return 0.20;
    if (year >= 6 && year <= 10)
        return 0.02;
    return 0.0;
};
exports.getInitialChargeRate = getInitialChargeRate;
const getSurrenderChargeRate = (year) => {
    if (year === 1)
        return 1.00;
    if (year === 2)
        return 0.90;
    if (year === 3)
        return 0.45;
    if (year === 4)
        return 0.30;
    if (year === 5)
        return 0.20;
    if (year === 6)
        return 0.10;
    return 0.0;
};
exports.getSurrenderChargeRate = getSurrenderChargeRate;
/**
 * EPOS Policy Administrative Fee Schedule (Phí Quản Lý Hợp Đồng hàng tháng)
 * Year 1: 578k/yr (~48,167/mo)
 * Year 2: 602k/yr (~50,167/mo)
 * Year 3: 626k/yr (~52,167/mo)
 * Year 4: 650k/yr (~54,167/mo)
 * Year 5: 674k/yr (~56,167/mo)
 * Year 6: 698k/yr (~58,167/mo)
 * Year 7: 722k/yr (~60,167/mo)
 * Year 8: 746k/yr (~62,167/mo)
 * Year 12: 835k/yr (~69,583/mo)
 * Year 13+: capped at 840k/yr (70,000/mo)
 */
const getMonthlyPolicyFee = (year) => {
    if (year >= 13)
        return 70000;
    if (year === 12)
        return Math.round(835000 / 12);
    const annualFee = 578000 + Math.max(0, year - 1) * 24000;
    return Math.round(annualFee / 12);
};
exports.getMonthlyPolicyFee = getMonthlyPolicyFee;
const getGuaranteedInterestRate = (year) => {
    if (year <= 2)
        return 0.030;
    if (year <= 4)
        return 0.025;
    if (year <= 6)
        return 0.020;
    if (year <= 8)
        return 0.015;
    if (year <= 10)
        return 0.010;
    return 0.005;
};
exports.getGuaranteedInterestRate = getGuaranteedInterestRate;
const MALE_COI_TABLE = {
    30: 0.00137917,
    31: 0.00139939,
    32: 0.00143947,
    33: 0.00149983,
    34: 0.00155998,
    35: 0.00164019,
    36: 0.00174071,
    37: 0.00186096,
    38: 0.00199174,
    39: 0.00215260,
    40: 0.00233311,
    41: 0.00253531,
    42: 0.00275791,
    43: 0.00299066,
    44: 0.00324390,
    45: 0.00352740,
    46: 0.00381158,
    47: 0.00413669,
    48: 0.00446202,
    49: 0.00482849,
    50: 0.00545000,
    51: 0.00597000,
    52: 0.00655000,
    53: 0.00720000,
    54: 0.00790000,
};
/**
 * EPOS Sum Assured (STBH) Min/Max Business Constraint Rules
 * - Age <= 30: Min 55x, Max 120x (For 20M premium: 1.1 tỷ - 2.4 tỷ)
 * - Age 31-40: Min 45x, Max 100x
 * - Age 41-50: Min 35x, Max 80x
 * - Age 51-60: Min 25x, Max 60x
 * - Age > 60:  Min 15x, Max 35x
 */
const calculateSAMinMax = (age, basePremium) => {
    const prem = Math.max(0, basePremium);
    let minMultiplier = 55;
    let maxMultiplier = 120;
    if (age > 60) {
        minMultiplier = 15;
        maxMultiplier = 35;
    }
    else if (age > 50) {
        minMultiplier = 25;
        maxMultiplier = 60;
    }
    else if (age > 40) {
        minMultiplier = 35;
        maxMultiplier = 80;
    }
    else if (age > 30) {
        minMultiplier = 45;
        maxMultiplier = 100;
    }
    const minSA = Math.max(100000000, Math.round(minMultiplier * prem));
    const maxSA = Math.min(10000000000, Math.max(minSA, Math.round(maxMultiplier * prem)));
    return { minSA, maxSA, minMultiplier, maxMultiplier };
};
exports.calculateSAMinMax = calculateSAMinMax;
/**
 * EPOS Chronological Age (Tuổi Sinh Nhật / Tuổi Thực) Calculation Rule
 * Exact age based on completed birth anniversary relative to reference date.
 * For DOB 25/11/1996 on date 12/08/2026:
 * - Birthday 25/11/2026 has not passed yet in August 2026.
 * - Chronological Age = 29.
 */
const calculateChronologicalAge = (dobStr, refDate = new Date(2026, 7, 12)) => {
    if (!dobStr)
        return 29;
    let dobDay = 1, dobMonth = 1, dobYear = 1996;
    if (dobStr.includes('/')) {
        const parts = dobStr.split('/').map(p => parseInt(p, 10));
        if (parts.length === 3) {
            dobDay = parts[0];
            dobMonth = parts[1];
            dobYear = parts[2];
        }
    }
    else if (dobStr.includes('-')) {
        const parts = dobStr.split('-').map(p => parseInt(p, 10));
        if (parts.length === 3) {
            dobYear = parts[0];
            dobMonth = parts[1];
            dobDay = parts[2];
        }
    }
    let age = refDate.getFullYear() - dobYear;
    const m = refDate.getMonth() - (dobMonth - 1);
    if (m < 0 || (m === 0 && refDate.getDate() < dobDay)) {
        age--;
    }
    return Math.max(0, age);
};
exports.calculateChronologicalAge = calculateChronologicalAge;
/**
 * EPOS Insurance Age (Tuổi Bảo Hiểm) Calculation Rule
 * Based on Nearest Birthday relative to reference date.
 * For DOB 25/11/1996 on date 12/08/2026:
 * - Difference = 29 years, 8 months, 18 days.
 * - Since remaining months >= 6, Insurance Age = 30.
 */
const calculateInsuranceAge = (dobStr, refDate = new Date(2026, 7, 12)) => {
    if (!dobStr)
        return 30;
    let dobDay = 1, dobMonth = 1, dobYear = 1996;
    if (dobStr.includes('/')) {
        const parts = dobStr.split('/').map(p => parseInt(p, 10));
        if (parts.length === 3) {
            dobDay = parts[0];
            dobMonth = parts[1];
            dobYear = parts[2];
        }
    }
    else if (dobStr.includes('-')) {
        const parts = dobStr.split('-').map(p => parseInt(p, 10));
        if (parts.length === 3) {
            dobYear = parts[0];
            dobMonth = parts[1];
            dobDay = parts[2];
        }
    }
    const monthsDiff = (refDate.getFullYear() - dobYear) * 12 + (refDate.getMonth() - (dobMonth - 1));
    const exactYears = monthsDiff / 12;
    return Math.max(0, Math.round(exactYears));
};
exports.calculateInsuranceAge = calculateInsuranceAge;
const getMonthlyMortalityRate = (age, gender) => {
    const roundedAge = Math.floor(age);
    let maleRate = 0.001850;
    if (MALE_COI_TABLE[roundedAge] !== undefined) {
        maleRate = MALE_COI_TABLE[roundedAge];
    }
    else if (roundedAge < 30) {
        maleRate = MALE_COI_TABLE[30] * Math.pow(0.97, 30 - roundedAge);
    }
    else {
        maleRate = MALE_COI_TABLE[54] * Math.pow(1.08, roundedAge - 54);
    }
    const isFemale = gender === 'Nữ' || gender === 'Nu' || gender === 'FEMALE';
    const rate = isFemale ? maleRate * 0.70 : maleRate;
    return rate / 12;
};
exports.getMonthlyMortalityRate = getMonthlyMortalityRate;
const simulateScenario = (policyYear, currentAge, gender, sumAssured, annualInterestRate, allocatedBasePremium, allocatedTopUpPremium, prevState, withdrawalBase, withdrawalTopUp, loyaltyBonus) => {
    let baseAV = Math.max(0, prevState.baseAV + allocatedBasePremium - withdrawalBase);
    let topUpAV = Math.max(0, prevState.topUpAV + allocatedTopUpPremium - withdrawalTopUp);
    let accumulatedLoyaltyBonus = prevState.accumulatedLoyaltyBonus || 0;
    const roundedAge = Math.floor(currentAge);
    const annualMortalityRate = (0, exports.getMonthlyMortalityRate)(roundedAge, gender) * 12;
    let totalCOI = 0;
    let totalPolicyFee = 0;
    let totalInterest = 0;
    for (let m = 1; m <= 12; m++) {
        const ageAtMonth = currentAge + (m - 1) / 12;
        const totalAV = baseAV + topUpAV;
        // Death Benefit: Max(sumAssured + accumulatedLoyaltyBonus, totalAV)
        const deathBenefitMonth = Math.max(sumAssured + accumulatedLoyaltyBonus, totalAV);
        const nar = Math.max(0, deathBenefitMonth - totalAV);
        const monthlyCOI = (nar * annualMortalityRate) / 12;
        const monthlyPolicyFee = (0, exports.getMonthlyPolicyFee)(policyYear);
        // Deduct monthly fees
        const totalDeductionMonth = monthlyCOI + monthlyPolicyFee;
        if (baseAV >= totalDeductionMonth) {
            baseAV -= totalDeductionMonth;
        }
        else {
            const remainingDeduction = totalDeductionMonth - baseAV;
            baseAV = 0;
            topUpAV = Math.max(0, topUpAV - remainingDeduction);
        }
        // Add monthly interest credited on net account
        const monthlyInterestBase = baseAV * (annualInterestRate / 12);
        const monthlyInterestTopUp = topUpAV * (annualInterestRate / 12);
        baseAV += monthlyInterestBase;
        topUpAV += monthlyInterestTopUp;
        totalInterest += monthlyInterestBase + monthlyInterestTopUp;
        totalCOI += monthlyCOI;
        totalPolicyFee += monthlyPolicyFee;
    }
    // Credit Loyalty Bonus at Year End
    if (loyaltyBonus > 0) {
        baseAV += loyaltyBonus;
        accumulatedLoyaltyBonus += loyaltyBonus;
    }
    const finalTotalAV = baseAV + topUpAV;
    const finalDeathBenefit = Math.max(sumAssured + accumulatedLoyaltyBonus, finalTotalAV);
    return {
        state: {
            baseAV,
            topUpAV,
            totalAV: finalTotalAV,
            accumulatedLoyaltyBonus,
        },
        totalCOI: Math.round(totalCOI),
        totalPolicyFee: Math.round(totalPolicyFee),
        totalInterest: Math.round(totalInterest),
        deathBenefit: Math.round(finalDeathBenefit),
    };
};
const calculateULCashflow = (input) => {
    const age = (0, exports.parseNumber)(input.age, 35);
    const gender = input.gender === 'Nữ' || input.gender === 'Nu' ? 'Nữ' : 'Nam';
    const initialSumAssured = (0, exports.parseNumber)(input.sumAssured, 800000000);
    const totalPremium = (0, exports.parseNumber)(input.totalPremium, 10000000);
    const term = (0, exports.parseNumber)(input.term, 20);
    const { selectedRiderIds = [], declaredInterestRate: rawRate, topUpAmount: rawTopUpAmount = 0, topUpStartYear: rawTopUpStartYear = 1, topUpDuration: rawTopUpDuration = 0, riderPremiumOverrides, productId = 'XUM', withdrawals = {}, } = input;
    const topUpAmount = (0, exports.parseNumber)(rawTopUpAmount, 0);
    const topUpStartYear = (0, exports.parseNumber)(rawTopUpStartYear, 1);
    const topUpDuration = (0, exports.parseNumber)(rawTopUpDuration, 0);
    const isILP = productId === 'XPQ' || productId === 'XTL' || productId === 'XTL-PS';
    const declaredInterestRate = rawRate !== undefined ? (0, exports.parseNumber)(rawRate, 0.04) : isILP ? 0.07 : 0.04;
    const rows = [];
    let projState = { baseAV: 0, topUpAV: 0, totalAV: 0, accumulatedLoyaltyBonus: 0 };
    let guarState = { baseAV: 0, topUpAV: 0, totalAV: 0, accumulatedLoyaltyBonus: 0 };
    let accumulatedPremium = 0;
    let accumulatedTopUp = 0;
    const ridersYear1Total = (0, exports.getTotalRiderPremium)(selectedRiderIds, 1, riderPremiumOverrides, age);
    const basePremium = Math.max(0, totalPremium - ridersYear1Total);
    const topUpActiveEndYear = topUpStartYear + Math.max(0, topUpDuration) - 1;
    const isTopUpActive = (year) => topUpAmount > 0 &&
        topUpDuration > 0 &&
        year >= topUpStartYear &&
        year <= topUpActiveEndYear &&
        year <= term;
    for (let y = 1; y <= 20; y++) {
        const currentAge = age + (y - 1);
        const ridersThisYear = y <= term ? (0, exports.getTotalRiderPremium)(selectedRiderIds, y, riderPremiumOverrides, currentAge) : 0;
        const premiumPaidThisYear = y <= term ? basePremium + ridersThisYear : 0;
        accumulatedPremium += premiumPaidThisYear;
        const topUpPaidThisYear = isTopUpActive(y) ? topUpAmount : 0;
        accumulatedTopUp += topUpPaidThisYear;
        const guaranteedRate = (0, exports.getGuaranteedInterestRate)(y);
        const yearDeclaredRate = y <= 10 ? declaredInterestRate : Math.min(declaredInterestRate, 0.04);
        const projectedRate = Math.max(yearDeclaredRate, guaranteedRate);
        let saIncreaseFactor = 1.00;
        const isFixedOption = input.stbhOption === 'KHONG_TU_DONG_GIA_TANG' ||
            productId === 'UL050 - Lựa chọn STBH không tự động gia tăng' ||
            productId === 'UL049 - Lựa chọn STBH không tự động gia tăng' ||
            productId?.startsWith('UL049');
        if (isFixedOption) {
            saIncreaseFactor = 1.00;
        }
        else if (productId === 'XTL' || productId === 'XTL-PS') {
            saIncreaseFactor = Math.min(1.75, 1.00 + Math.max(0, y - 1) * 0.15);
        }
        else {
            saIncreaseFactor = Math.min(1.50, 1.00 + Math.max(0, y - 1) * 0.05);
        }
        const sumAssured = initialSumAssured * saIncreaseFactor;
        let allocatedBasePremium = 0;
        let initialCharge = 0;
        if (y <= term) {
            initialCharge = basePremium * (0, exports.getInitialChargeRate)(y);
            allocatedBasePremium = basePremium - initialCharge;
        }
        // Loyalty Bonus (QL Duy Trì HĐ)
        let loyaltyBonus = 0;
        const isILPProduct = productId === 'XPQ' || productId === 'XTL' || productId === 'XTL-PS';
        if (isILPProduct) {
            if (y === 10 || y === 15 || y === 20) {
                loyaltyBonus = basePremium * 0.50;
            }
        }
        else {
            if (y === 10) {
                loyaltyBonus = basePremium * 0.50; // EPOS UL050 50% Loyalty Bonus at Year 10
            }
        }
        const wBase = withdrawals[y]?.base ?? 0;
        const wTopUp = withdrawals[y]?.topUp ?? 0;
        const wTotal = wBase + wTopUp;
        // Simulate Projected Scenario
        const projSim = simulateScenario(y, currentAge, gender, sumAssured, projectedRate, allocatedBasePremium, topUpPaidThisYear, projState, wBase, wTopUp, loyaltyBonus);
        // Simulate Guaranteed Scenario
        const guarSim = simulateScenario(y, currentAge, gender, sumAssured, guaranteedRate, allocatedBasePremium, topUpPaidThisYear, guarState, wBase, wTopUp, loyaltyBonus);
        projState = projSim.state;
        guarState = guarSim.state;
        const surrenderCharge = basePremium * (0, exports.getSurrenderChargeRate)(y);
        const surrenderValueProjected = Math.max(0, projState.totalAV - surrenderCharge);
        const surrenderValueGuaranteed = Math.max(0, guarState.totalAV - surrenderCharge);
        const coiProjected = Math.round(projSim.totalCOI);
        const coiGuaranteed = Math.round(guarSim.totalCOI);
        const policyFee = Math.round(projSim.totalPolicyFee);
        rows.push({
            year: y,
            age: currentAge,
            premiumPaid: Math.round(premiumPaidThisYear),
            topUpPaid: Math.round(topUpPaidThisYear),
            accumulatedPremium: Math.round(accumulatedPremium),
            accumulatedTopUp: Math.round(accumulatedTopUp),
            initialCharge: Math.round(initialCharge),
            allocatedBasePremium: Math.round(allocatedBasePremium),
            allocatedTopUp: Math.round(topUpPaidThisYear),
            riderPremium: Math.round(ridersThisYear),
            coi: coiProjected,
            policyFee,
            totalDeduction: coiProjected + policyFee,
            interestEarned: Math.round(projSim.totalInterest),
            accountValue: Math.round(projState.totalAV),
            surrenderValue: Math.round(surrenderValueProjected),
            stbhMainProduct: Math.round(sumAssured),
            loyaltyBonus: Math.round(loyaltyBonus),
            withdrawalTotal: Math.round(wTotal),
            withdrawalBase: Math.round(wBase),
            withdrawalTopUp: Math.round(wTopUp),
            accountValueProjected: Math.round(projState.totalAV),
            accountValueGuaranteed: Math.round(guarState.totalAV),
            surrenderValueProjected: Math.round(surrenderValueProjected),
            surrenderValueGuaranteed: Math.round(surrenderValueGuaranteed),
            deathBenefitProjected: Math.round(projSim.deathBenefit),
            deathBenefitGuaranteed: Math.round(guarSim.deathBenefit),
            baseAccountValueProjected: Math.round(projState.baseAV),
            baseAccountValueGuaranteed: Math.round(guarState.baseAV),
            topUpAccountValueProjected: Math.round(projState.topUpAV),
            topUpAccountValueGuaranteed: Math.round(guarState.topUpAV),
            coiProjected,
            coiGuaranteed,
            interestBonusProjected: Math.round(projSim.totalInterest),
            interestBonusGuaranteed: Math.round(guarSim.totalInterest),
        });
    }
    return rows;
};
exports.calculateULCashflow = calculateULCashflow;
